import {documentToReactComponents, Options} from '@contentful/rich-text-react-renderer'
import {Table, Text as RichText} from '@contentful/rich-text-types'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {
  inlineFallbackRenderNode,
  renderHyperlink,
  renderMark,
} from '@/components/contentful/styledOptions'
import {theme} from '@/theme/theme'
import {getHeadingElement} from '@/utils/contentful/headingLevels'
import {normalizeContentfulHref} from '@/utils/contentful/helpers'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

export const options: Options = {
  renderNode: {
    ['heading-2']: (node) => {
      const heading = (node.content[0] as RichText).value
      return (
        <>
          <div className="title">
            <Text variant={TextVariant.h4} element={getHeadingElement(node, 2)}>
              {heading}
            </Text>
          </div>
          <style jsx>{`
            .title:not(:first-child) {
              margin-top: ${theme.spacing[8]};
            }
          `}</style>
        </>
      )
    },
    ['heading-3']: (node) => {
      const heading = (node.content[0] as RichText).value
      return (
        <>
          <div className="title">
            <Text
              variant={TextVariant.h5}
              element={getHeadingElement(node, 3)}
              fontWeight={TextFontWeight.bold}
            >
              {heading}
            </Text>
          </div>
          <style jsx>{`
            .title {
              margin-top: ${theme.spacing[5]};
            }
          `}</style>
        </>
      )
    },
    ['paragraph']: (node, children) => {
      return (
        <>
          <div className="paragraph">
            <Text>{children}</Text>
          </div>
          <style jsx>{`
            .paragraph {
              margin-top: ${theme.spacing[4]};
            }
          `}</style>
        </>
      )
    },
    ['unordered-list']: (node, children) => (
      <>
        <ul className="list">{children}</ul>
        <style jsx>{`
          .list {
            margin-top: ${theme.spacing[4]};
            margin-left: ${theme.spacing[5]};
          }
          @media ${MOBILE_SCREENSIZE} {
            .list {
              margin-left: ${theme.spacing[4]};
            }
          }
        `}</style>
      </>
    ),
    ['list-item']: (node) => {
      const UnTaggedChildren = documentToReactComponents(node as any, {
        renderMark,
        renderNode: {
          ...inlineFallbackRenderNode,
          ['list-item']: (node, children) => children,
          ['paragraph']: (node, children) => children,
          ['hyperlink']: renderHyperlink,
        },
      })

      return (
        <>
          <li className="item">
            <Text>{UnTaggedChildren}</Text>
          </li>
          <style jsx>{`
            .item {
              list-style-type: disc;
              margin-top: ${theme.spacing[3]};
            }
          `}</style>
        </>
      )
    },
    ['table']: (node) => {
      const table = node as Table
      return (
        <>
          <div className="table-wrapper">
            <table className="table">
              <thead>
                {table.content.map(
                  (row, rowIndex) =>
                    rowIndex === 0 && (
                      <tr key={rowIndex}>
                        {row.content.map((cell, cellIndex) => (
                          <th key={cellIndex}>
                            <Text variant={TextVariant.text14}>
                              {(cell.content[0].content[0] as RichText).value}
                            </Text>
                          </th>
                        ))}
                      </tr>
                    ),
                )}
              </thead>
              <tbody key="body">
                {table.content.map(
                  (row, rowIndex) =>
                    rowIndex > 0 && (
                      <tr key={rowIndex}>
                        {row.content.map((cell, cellIndex) => (
                          <td key={cellIndex}>
                            <Text variant={TextVariant.text14}>
                              {documentToReactComponents(cell.content[0] as any, {
                                renderMark,
                                renderNode: {
                                  ...inlineFallbackRenderNode,
                                  ['hyperlink']: (node, children) => (
                                    <a
                                      href={normalizeContentfulHref(node.data.uri)}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {children}
                                    </a>
                                  ),
                                },
                              })}
                            </Text>
                          </td>
                        ))}
                      </tr>
                    ),
                )}
              </tbody>
            </table>
          </div>
          <style jsx>{`
            .table-wrapper {
              margin-top: ${theme.spacing[6]};
            }
            .table {
              width: 100%;
              background-color: ${theme.palette.background.default};
            }
            th,
            td {
              padding: ${theme.spacing[2]};
              vertical-align: top;
              border: 2px solid ${theme.palette.butter[200]};
            }
            th {
              font-weight: ${theme.fontWeight.semibold};
              background-color: ${theme.palette.background.butter};
            }
            td {
              white-space: pre-line;
            }
            @media ${MOBILE_SCREENSIZE} {
              .table-wrapper {
                margin-top: ${theme.spacing[4]};
                margin-right: -${theme.spacing[3]};
                max-width: calc(100vw - ${theme.spacing[3]});
                overflow-x: auto;
              }
              th {
                min-width: 100px;
              }
            }
          `}</style>
        </>
      )
    },
    ...inlineFallbackRenderNode,
    ['hyperlink']: renderHyperlink,
  },
  renderMark,
}
